import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Box,
  TextField,
  InputAdornment,
  Pagination,
  FormControl,
  Select,
  MenuItem
} from '@mui/material';
import { Search, Subscriptions, CalendarMonth, Person, FilterList } from '@mui/icons-material';
import axios from 'axios';
import { api } from "../context/AuthContext";

function SuperAdminSubscriptions() {
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [page, setPage] = useState(1);

  const formatPlanName = (planCode) => {
    if (!planCode) return 'N/A';
    return planCode
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const renderSubscriptionPeriod = (sub) => {
    const plan = sub.plan_code?.toUpperCase();
    const isOneTime = plan === 'PAY_AS_YOU_GO' || plan === 'OVERAGE_JOB';
    
    const formatDate = (dateString) => {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'N/A';
      
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    };

    const start = formatDate(sub.start_date || sub.created_at);
    let end = formatDate(sub.end_date || sub.expires_at);

    if (isOneTime && end === 'N/A') {
      return 'One Time Purchase';
    }

    if (end === 'N/A') {
      // Fallback calculation if expires_at is missing but it's a recurring plan
      const startDate = new Date(sub.created_at);
      if (!isNaN(startDate.getTime())) {
        const endDate = new Date(startDate);
        if (plan?.includes('MONTHLY')) {
          endDate.setMonth(startDate.getMonth() + 1);
        } else if (plan?.includes('ANNUAL')) {
          endDate.setFullYear(startDate.getFullYear() + 1);
        } else {
          return `${start} - N/A`;
        }
        return `${start} - ${formatDate(endDate)}`;
      }
    }

    return `${start} - ${end}`;
  };

  useEffect(() => {
    async function fetchSubscriptions() {
      try {
        // We can reuse the super-admin payments endpoint as subscriptions are based on payment orders
        const response = await api.get('/payment/super-admin/payments');
        // Filter only captured payments as they represent successful subscriptions
        setSubscriptions(response.data);
      } catch (error) {
        console.error('Failed to fetch subscription history:', error);
      } finally {
        setLoading(false);
      }
    }
    fetchSubscriptions();
  }, []);

  const filteredSubscriptions = subscriptions.filter((sub) => {
    const searchStr = searchTerm.toLowerCase();
    const formattedPlan = formatPlanName(sub.plan_code).toLowerCase();
    const matchesSearch = (
      (sub.first_name?.toLowerCase() || '').includes(searchStr) ||
      (sub.last_name?.toLowerCase() || '').includes(searchStr) ||
      (sub.email?.toLowerCase() || '').includes(searchStr) ||
      (sub.plan_code?.toLowerCase() || '').includes(searchStr) ||
      formattedPlan.includes(searchStr) ||
      (sub.razorpay_order_id?.toLowerCase() || '').includes(searchStr)
    );

    const matchesFilter = statusFilter === 'All' || 
                          (statusFilter === 'Captured' && sub.status === 'captured') ||
                          (statusFilter === 'Expired' && sub.status === 'expired');

    return matchesSearch && matchesFilter;
  });

  const paginatedSubscriptions = filteredSubscriptions.slice((page - 1) * 10, page * 10);
  const totalPages = Math.ceil(filteredSubscriptions.length / 10);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container sx={{ py: 4 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          mb: 4,
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Box>
          {/*<Typography
            sx={{
              color: "#1E3A8A",
              fontSize: {
                xs: "2rem",
                sm: "2.3rem",
                md: "2.6rem",
              },
              fontWeight: 800,
              lineHeight: 1.2,
              mb: 0.8,
            }}
          >*/}
          <Typography
              variant="h4"
              sx={{
                color: "#1E3A8A",
                fontWeight: "bold",
                fontSize: { xs: "1.5rem", md: "2rem" }
              }}
          >
            Subscription History
          </Typography>

          <Typography
            sx={{
              color: "text.secondary",
              fontSize: "1rem",
              fontWeight: 400,
            }}
          >
            Review subscription plans, billing status, and payment history across all users.
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder="Search by User"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setPage(1);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
            sx={{ width: 300 }}
          />
          <FormControl size="small">
            <Select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(1);
              }}
              displayEmpty
              renderValue={() => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#1E3A8A', fontWeight: 'bold' }}>
                  <FilterList sx={{ fontSize: 20 }} />
                  Filters
                </Box>
              )}
              sx={{
                borderRadius: 0,
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#1E3A8A',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#1E40AF',
                },
                '& .MuiSelect-select': {
                  display: 'flex',
                  alignItems: 'center',
                }
              }}
            >
              <MenuItem disabled sx={{ fontWeight: 'bold', color: '#1E3A8A', borderBottom: '1px solid #eee', justifyContent: 'center', opacity: '1 !important' }}>
                Filters
              </MenuItem>
              <MenuItem value="All">All</MenuItem>
              <MenuItem value="Captured">Active</MenuItem>
              <MenuItem value="Expired">Expired</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>

      <TableContainer component={Paper} sx={{ mt: 2, borderRadius: 0, boxShadow: 3 }}>
        <Table>
          <TableHead sx={{ bgcolor: '#f5f5f5' }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>User</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Plan Details</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Amount</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Subscription Period</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Transaction ID</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedSubscriptions.map((sub) => (
              <TableRow key={sub.id} hover>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Person color="action" fontSize="small" />
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        {sub.first_name} {sub.last_name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {sub.email}
                      </Typography>
                    </Box>
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip 
                    label={formatPlanName(sub.plan_code)} 
                    size="small" 
                    color="primary" 
                    variant="outlined" 
                    sx={{ fontWeight: 'bold' }}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                    ₹{sub.amount / 100}
                  </Typography>
                </TableCell>
                {/*<TableCell>
                  <Chip 
                    label={sub.status} 
                    color={sub.status === 'captured' ? 'success' : (sub.status === 'failed' ? 'error' : 'warning')} 
                    size="small" 
                  />
                </TableCell>*/}
                <TableCell>
                  <Chip
                      label={sub.status === "captured" ? "Active" : sub.status}
                      color={sub.status === "captured" ? "success" : sub.status === "failed" ? "error" : "warning"}
                      size="small"
                  />
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <CalendarMonth fontSize="inherit" color="action" />
                    <Typography variant="caption">
                      {renderSubscriptionPeriod(sub)}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                    {sub.payment_id || sub.razorpay_order_id || 'N/A'}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
            {filteredSubscriptions.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">No subscription records found</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mt: 3,
            flexWrap: "wrap",
            gap: 2,
          }}
      >
        <Typography variant="body2" color="text.secondary">
          Showing {paginatedSubscriptions.length > 0 ? (page - 1) * 10 + 1 : 0} to{" "}
          {Math.min(page * 10, filteredSubscriptions.length)} of{" "}
          {filteredSubscriptions.length} records
        </Typography>
        <Pagination
            count={totalPages}
            page={page}
            onChange={(e, value) => setPage(value)}
            shape="rounded"
            color="primary"
            sx={{
              "& .MuiPaginationItem-root.Mui-selected": {
                backgroundColor: "#1E3A8A",
                "&:hover": {
                  backgroundColor: "#1E40AF",
                },
              },
            }}
        />
      </Box>
    </Container>
  );
}

export default SuperAdminSubscriptions;
